import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { MfaFactor, maskPhone, startChallenge, verifyChallenge } from "@/hooks/use-mfa";
import { useBrand } from "@/contexts/BrandContext";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";

interface Props {
  onVerified: () => void;
  onSignOut: () => void;
}

/** Second sign-in step for users who have turned on two-step verification. */
export function MfaChallenge({ onVerified, onSignOut }: Props) {
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  const [factor, setFactor] = useState<MfaFactor | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { brand, isWhiteLabel } = useBrand();
  const logo = brand?.config.logo_url;

  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const list = ((data?.all ?? []) as MfaFactor[]).filter((f) => f.status === "verified");
      setFactors(list);
      setFactor(list.find((f) => f.factor_type === "totp") ?? list[0] ?? null);
    });
  }, []);

  useEffect(() => {
    if (!factor) return;
    setChallengeId(null);
    setCode("");
    startChallenge(factor.id)
      .then(setChallengeId)
      .catch((e) => toast.error(e.message || "Could not start verification"));
  }, [factor]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factor || !challengeId) return;
    setBusy(true);
    try {
      await verifyChallenge(factor.id, challengeId, code.trim());
      onVerified();
    } catch (err: any) {
      toast.error(err.message || "That code didn't work. Try again.");
      // Challenges are single-use; start a fresh one.
      startChallenge(factor.id).then(setChallengeId).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const resend = () => factor && startChallenge(factor.id).then((id) => { setChallengeId(id); toast.success("New code sent"); }).catch((e) => toast.error(e.message));

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm p-6 space-y-5">
        {logo && <img src={logo} alt="" className="brand-mfa-logo mx-auto object-contain" style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined} />}
        <div className="text-center space-y-1">
          <ShieldCheck className="h-8 w-8 mx-auto text-primary" />
          <h1 className="text-xl font-semibold">Two-step verification</h1>
          <p className="text-sm text-muted-foreground">
            {factor?.factor_type === "phone"
              ? `We texted a code to ${maskPhone(factor.phone)}`
              : "Enter the 6-digit code from your authenticator app"}
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            className="text-center tracking-widest text-lg"
            autoFocus
          />
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6 || !challengeId}>
            {busy ? "Checking..." : "Verify"}
          </Button>
        </form>
        <div className="flex flex-col gap-1 text-sm">
          {factor?.factor_type === "phone" && (
            <Button variant="ghost" size="sm" onClick={resend}>Resend code</Button>
          )}
          {factors.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFactor(factors.find((f) => f.id !== factor?.id) ?? null)}
            >
              Use {factor?.factor_type === "phone" ? "authenticator app" : "text message"} instead
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onSignOut}>Sign out</Button>
        </div>
      </Card>
    </div>
  );
}
